/**
 * The rules of a single block edit: write the cell, relight, and apply what
 * the change does to its neighbours (door halves, unsupported flora, torches,
 * rails, stairs/fences/walls/panes). DOM-free: the client's ChunkManager and
 * the game server (src/server/serverWorld.ts) both edit the world through this.
 */

import { WORLD_HEIGHT, CHUNK_SIZE } from '../constants';
import {
  AIR, DOOR_BOTTOM_TO_TOP, DOOR_TOP_TO_BOTTOM, FACE_DIRS, heldBy, IS_SOLID, IS_TORCH, NEEDS_RESHAPE, NEEDS_SUPPORT,
  RAIL_SHAPE, reshapeId, solidNeighborMask, WATER_AMOUNT,
} from '../blocks';
import { CHUNK_AREA, CHUNK_VOLUME, TOP_MARGIN, worldKey, type World } from './chunk';
import { relightAfterEdit } from './lighting';
import { settleRails } from './rails';

/** Cells whose shape depends on (wx, wz)'s block: itself and its four neighbours. */
const RESHAPE_CELLS: readonly (readonly [number, number])[] = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]];

export interface EditHooks {
  /** Every cell this edit wrote, the requested one and each cascade (chunk coords + voxel index). */
  record(cx: number, cz: number, index: number, id: number): void;
  /** A full edit of another cell caused by this one (a torch losing its hold, a rail re-shaping). */
  setBlock(wx: number, wy: number, wz: number, id: number): void;
  /** A supported block popped off, or water washed a plant away: the owner drops its loot. */
  popped?(wx: number, wy: number, wz: number, oldId: number): void;
  /** Stops the rail cascade re-entering; the owner keeps one per world. */
  railBusy: boolean;
}

/**
 * Set (wx, wy, wz) to `id`. Returns -1 when nothing changed (out of range, no
 * chunk, same id), otherwise how many supported blocks above the cell broke.
 * `dirty` collects the keys of every chunk whose blocks or light changed,
 * including neighbours whose border faces now look different.
 */
export function editBlock(world: World, wx: number, wy: number, wz: number, id: number, hooks: EditHooks, dirty: Set<number>): number {
  if (wy < 0 || wy >= WORLD_HEIGHT) return -1;
  const cx = wx >> 4;
  const cz = wz >> 4;
  const chunk = world.getChunk(cx, cz);
  if (!chunk) return -1;
  const lx = wx & 15;
  const lz = wz & 15;
  const idx = lx + lz * CHUNK_SIZE + wy * CHUNK_AREA;
  if (chunk.blocks.length < CHUNK_VOLUME) {
    // a short chunk (chunk.ts): above its arrays is air already; a block grows them, keeping the margin its light needs
    if (id !== AIR) chunk.ensureHeight(wy + 1 + TOP_MARGIN);
    else if (idx >= chunk.blocks.length) return -1;
  }
  const oldId = chunk.blocks[idx];
  if (oldId === id) return -1;
  chunk.blocks[idx] = id;
  hooks.record(cx, cz, idx, id);

  relightAfterEdit(world, wx, wy, wz, oldId, id, dirty);

  // Door halves travel together when a door is *broken*: clearing the top
  // also clears the bottom (and vice versa), so a half-orphan can never
  // linger in the world. Only a removal counts — toggling a door open swaps
  // each half for its open id, and treating that as a break used to delete
  // the other half (the bottom half vanished as soon as you used the door).
  const removed = id === AIR || WATER_AMOUNT[id] > 0;
  // water flowing into a plant washes it out (vanilla drops it)
  if (WATER_AMOUNT[id] > 0 && NEEDS_SUPPORT[oldId] === 1 && WATER_AMOUNT[oldId] === 0) hooks.popped?.(wx, wy, wz, oldId);
  const topPartner = DOOR_TOP_TO_BOTTOM[oldId];
  const botPartner = DOOR_BOTTOM_TO_TOP[oldId];
  if (removed && topPartner !== undefined && wy - 1 >= 0) {
    const below = idx - CHUNK_AREA; // bottom half sits below the broken top
    const oldBelow = chunk.blocks[below];
    if (oldBelow === topPartner) {
      chunk.blocks[below] = AIR;
      hooks.record(cx, cz, below, AIR);
      relightAfterEdit(world, wx, wy - 1, wz, oldBelow, AIR, dirty);
    }
  } else if (removed && botPartner !== undefined && wy + 1 < WORLD_HEIGHT) {
    const above = idx + CHUNK_AREA;
    const oldAbove = chunk.blocks[above];
    if (oldAbove === botPartner) {
      chunk.blocks[above] = AIR;
      hooks.record(cx, cz, above, AIR);
      relightAfterEdit(world, wx, wy + 1, wz, oldAbove, AIR, dirty);
    }
  }

  // Support rule: flora, torches and cacti break when the block below them
  // is gone (or flooded). Removing a block cascades upward through a supported column.
  let broken = 0;
  if (removed && wy + 1 < WORLD_HEIGHT) {
    const column = chunk.blocks;
    let above = idx + CHUNK_AREA;
    let y = wy + 1;
    while (y < WORLD_HEIGHT && NEEDS_SUPPORT[column[above]] === 1) {
      const old = column[above];
      hooks.popped?.(wx, y, wz, old);
      column[above] = AIR;
      hooks.record(cx, cz, above, AIR);
      relightAfterEdit(world, wx, y, wz, old, AIR, dirty);
      above += CHUNK_AREA;
      y++;
      broken++;
    }
  }

  // A torch holds on to whatever it was clicked on — a floor or a wall — so
  // it drops when that neighbour goes. The column walk above only looks up,
  // which would leave a wall torch hanging in mid-air.
  if (removed) {
    for (const [dx, dy, dz] of FACE_DIRS) {
      const nx = wx + dx, ny = wy + dy, nz = wz + dz;
      if (ny < 0 || ny >= WORLD_HEIGHT) continue;
      const nid = world.getBlock(nx, ny, nz);
      if (IS_TORCH[nid] !== 1) continue;
      const held = solidNeighborMask((ox, oy, oz) =>
        IS_SOLID[world.getBlock(nx + ox, ny + oy, nz + oz)] === 1);
      if (!heldBy(nid, held)) hooks.setBlock(nx, ny, nz, AIR);
    }
  }

  // rails join and re-shape against their neighbours (world/rails.ts); `railBusy` stops the cascade re-entering
  if (!hooks.railBusy && (RAIL_SHAPE[id] >= 0 || RAIL_SHAPE[oldId] >= 0 || broken > 0)) {
    hooks.railBusy = true;
    try {
      settleRails((x, y, z) => world.getBlock(x, y, z), (x, y, z, n) => { hooks.setBlock(x, y, z, n); },
        wx, wy + (RAIL_SHAPE[id] >= 0 || RAIL_SHAPE[oldId] >= 0 ? 0 : 1), wz);
    } finally { hooks.railBusy = false; }
  }

  // stairs, fences, walls and panes read their neighbours: re-settle this cell and the four beside it
  for (const [dx, dz] of RESHAPE_CELLS) {
    const nx = wx + dx, nz = wz + dz;
    const nc = world.getChunk(nx >> 4, nz >> 4);
    if (!nc) continue;
    const ni = (nx & 15) + (nz & 15) * CHUNK_SIZE + wy * CHUNK_AREA;
    if (ni >= nc.blocks.length) continue;
    const cur = nc.blocks[ni];
    if (NEEDS_RESHAPE[cur] !== 1) continue;
    const next = reshapeId(cur, (ox, oz) => world.getBlock(nx + ox, wy, nz + oz));
    if (next === cur) continue;
    nc.blocks[ni] = next;
    hooks.record(nx >> 4, nz >> 4, ni, next);
    relightAfterEdit(world, nx, wy, nz, cur, next, dirty);
    dirty.add(worldKey(nx >> 4, nz >> 4));
  }

  // this chunk + border neighbours (AO changes across borders)
  dirty.add(worldKey(cx, cz));
  if (lx === 0) dirty.add(worldKey(cx - 1, cz));
  if (lx === 15) dirty.add(worldKey(cx + 1, cz));
  if (lz === 0) dirty.add(worldKey(cx, cz - 1));
  if (lz === 15) dirty.add(worldKey(cx, cz + 1));
  if (lx === 0 && lz === 0) dirty.add(worldKey(cx - 1, cz - 1));
  if (lx === 0 && lz === 15) dirty.add(worldKey(cx - 1, cz + 1));
  if (lx === 15 && lz === 0) dirty.add(worldKey(cx + 1, cz - 1));
  if (lx === 15 && lz === 15) dirty.add(worldKey(cx + 1, cz + 1));
  return broken;
}
